import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import YupPassword from 'yup-password';

import { PSButton } from '@/components/ui/button';

import HttpService from '../../app/http';
import BsInputPassword from '../../components/common/BsInputPassword';
import SiteFooterComponent from '../../components/site/site-footer.component';
import SiteNavbarComponent from '../../components/site/site-navbar.component';
import { AlertsType } from '../../constants/alerts.constant';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import useAppLocalStorage from '../../hooks/useAppLocalStorage';
import useAuth from '../../hooks/useAuth';
import { WrappedErrorBoundary } from '../WrappedErrorBoundary';
import PassWordValidationChecker from './PassWordValidationChecker';

YupPassword(Yup);

const ResetPassword = () => {
  const { i18n, t } = useTranslation();
  const localStorage = useAppLocalStorage();
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [apiErrors, setApiErrors] = useState<any>({});

  const schema = Yup.object({
    new_password: Yup.string()
      .min(8, t(AlertsType.ISATKO004))
      .minLowercase(1, t(AlertsType.ISATKO005))
      .minUppercase(1, t(AlertsType.ISATKO006))
      .minSymbols(1, t(AlertsType.ISATKO007))
      .minNumbers(1, t(AlertsType.ISATKO008))
      .required()
      .label('New password'),
    new_confirm_password: Yup.string()
      .oneOf([Yup.ref('new_password')], t(AlertsType.ISATKO012))
      .label('Confirm new password')
      .required(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
  } = useForm<{ new_password: string; new_confirm_password: string }>({
    resolver: yupResolver(schema),
    mode: 'all',
  });

  const from = `/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`;

  const rules = [
    {
      id: 1,
      regex: '^.{8,32}$',
      textTranslated: t(AlertsType.ISATKO004),
      text: 'Must be 8 to 32 character',
      isPassed: false,
    },
    {
      id: 2,
      regex: '(?=.*[a-z])',
      textTranslated: t(AlertsType.ISATKO005),
      text: 'At least one lower case letter exists',
      isPassed: false,
    },
    {
      id: 3,
      regex: '(?=.*[A-Z])',
      textTranslated: t(AlertsType.ISATKO006),
      text: 'At least one upper case letter exists',
      isPassed: false,
    },
    {
      id: 4,
      regex: '(?=.*\\d)',
      textTranslated: t(AlertsType.ISATKO008),
      text: 'At least one digit exists',
      isPassed: false,
    },
    {
      id: 5,
      regex: '(?=.*?[.#?!@$ %^&*-])',
      textTranslated: t(AlertsType.ISATKO007),
      text: 'At least one special character exists',
      isPassed: false,
    },
  ];

  const ResetPasswordMutation = useMutation<any, AxiosError<any, any>>(
    (data: any) => HttpService.patch(`auth/${searchParams.get('user_identification')}/${searchParams.get('token')}/change-password`, data),
    {
      onError: err => {
        if (err?.response?.data?.code == 'RSUSKO001') {
          setApiErrors({
            ...apiErrors,
            new_password: t(AlertsType.ISATKO013),
          });
          setError('new_password', {
            message: t(AlertsType.ISATKO013),
          });
        }
      },
    }
  );
  const PasswordCheckMutation = useMutation<any, AxiosError<any, any>, { password: string; level?: string | null }>(
    data => HttpService.post('/auth/check-password', data),
    {
      onError: err => {
        setApiErrors({ ...apiErrors, new_password: err.response?.data.message });
        setError('new_password', {
          message: err.response?.data?.message,
        });
      },
    }
  );

  const onSubmit = (data: any) => {
    PasswordCheckMutation.mutate(
      {
        password: data.new_password,
        level: localStorage.getItem(`LOUISEBOT_password_level`),
      },
      {
        onSuccess: () => {
          ResetPasswordMutation.mutate(data, {
            onSuccess: res => {
              navigate('../login');
            },
          });
        },
      }
    );
  };

  useEffect(() => {
    let cleaner = true;

    if (cleaner && auth.user.isAuthenticated) navigate(from);

    if (!searchParams.get('user_identification') && !searchParams.get('token')) navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}`);

    return () => {
      cleaner = false;
    };
  }, [auth, from, navigate, searchParams]);

  return (
    <div className="reset-password-page tw-flex tw-flex-col tw-min-h-screen">
      <SiteNavbarComponent theme="light" />

      <Container className="tw-py-10">
        <h1 className="font-sofiaSans fw-bold tw-text-center tw-mb-8">{t('ResetPassword/key-01')}</h1>

        <Row className="justify-content-center">
          <Col md={6} sm={10} xs={12}>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group>
                <BsInputPassword
                  label={t('ResetPassword/key-02')}
                  isInvalid={!!errors.new_password || !!apiErrors.new_password}
                  errorMessage={errors.new_password?.message || apiErrors.new_password}
                  className={'mb-3'}
                  rhfControls={register('new_password')}
                  isPasswordStrengthBar={true}
                />
                <Form.Control.Feedback type="invalid">{errors?.new_password?.message as string}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group>
                <BsInputPassword
                  label={t('ResetPassword/key-03')}
                  isInvalid={!!errors.new_confirm_password}
                  errorMessage={errors.new_confirm_password?.message as string}
                  className={'mb-3'}
                  rhfControls={register('new_confirm_password')}
                />
              </Form.Group>

              {watch('new_password') && watch('new_password').length > 0 && (
                <Form.Group className="mb-4 password-check">
                  <PassWordValidationChecker
                    password={watch('new_password')}
                    ruleSet={rules.map((rule: any) => {
                      return {
                        ...rule,
                        message: rule.textTranslated,
                      };
                    })}
                  />
                </Form.Group>
              )}

              <PSButton
                id="btn-reset-login"
                variant="primary"
                type="submit"
                className="w-100 rounded auth-btn d-flex justify-content-center md:tw-mt-14"
                size="lg"
                disabled={ResetPasswordMutation.isLoading || PasswordCheckMutation.isLoading}
                isLoading={ResetPasswordMutation.isLoading}
                loadingAnimation="grow"
                title={t('authPages/login/btn')}
              />
            </Form>
          </Col>
        </Row>
      </Container>

      <SiteFooterComponent theme="light" className="mt-auto" />
    </div>
  );
};

export default WrappedErrorBoundary(ResetPassword);
